import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { loadFonts } from '../apis/fonts/loadFonts';
import { PdpState } from '../store';

export type FontStyle = 'regular' | 'bold' | 'italic' | 'bolditalic';
export type FontLoadStatus = 'pending' | 'ready' | 'error';

export type FontFamily = {
    name: string;
    fonts: Font[];
};

export type Font = {
    url: string;
    style: FontStyle;
};

export const { actions, reducer } = createSlice({

    name: 'fonts',

    initialState: {
        loadStatus: 'pending' as FontLoadStatus,
        families: [] as FontFamily[]
    },

    reducers: {
        overwrite: (state, action: PayloadAction<FontFamily[]>) => ({
            ...state,
            families: action.payload
        })
    },

    extraReducers: builder => {
        builder
            .addCase(loadFonts.pending, state => void (state.loadStatus = 'pending'))
            .addCase(loadFonts.rejected, state => void (state.loadStatus = 'error'))
            .addCase(loadFonts.fulfilled, state => void (state.loadStatus = 'ready'));
    }
});

export const selectAreFontsReady = createSelector([
    (state: PdpState) => state.fonts.loadStatus
], status => status === 'ready');

export const selectFamilyFontsByName = (name: string) => createSelector([
    (state: PdpState) => state.fonts.families
], families => families.find(f => f.name === name)!.fonts);

export const selectFamilySupportsBoldByName = (state: PdpState, name: string) => {
    return createSelector([
        selectFamilyFontsByName(name)
    ], fonts => fonts.some(f => f.style.includes('bold')))(state);
};

export const selectFamilySupportsItalicByName = (state: PdpState, name: string) => {
    return createSelector([
        selectFamilyFontsByName(name)
    ], fonts => fonts.some(f => f.style.includes('italic')))(state);
};

export const { overwrite } = actions;